<template>
    <div class="emailinput formcontrol" :class="containerClasses">
        <label>{{ label }}</label>

        <input
            type="text"
            name="email"
            :autocomplete="props.allowAutocomplete ? 'on' : 'off'"
            v-model="inputValue"
            @keyup="onInputKeyUp"
            @focus="onInputFocus"
            @blur="onInputBlur"
            :disabled="props.disabled"
            ref="emailElement"
        />

        <!-- Validation Icon -->
        <div v-if="isIconVisible" class="icon" :class="{ active: isIconActive }">
            <i class="fas fa-check"></i>
        </div>

        <!-- Verification State -->
        <template v-if="isConfirmationStateVisible">
            <div v-if="isVerified" class="state confirmed">
                {{ translations.profile.emailConfirmed }}
            </div>
            <div v-if="!isVerified" class="state unconfirmed">
                {{ translations.profile.emailUnconfirmed }}
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import { Validation } from "@/data/util/Validation";
import { translations } from "@/shared/localization/translations";
import { computed, ref, watch } from "vue";
import { InputRequirementType } from "@/components/elements/InputRequirementType";

// Props
const props = withDefaults(
    defineProps<{
        indent?: boolean;
        disabled?: boolean;
        value?: string;
        verified?: boolean;
        allowAutocomplete?: boolean;
        displayIcon?: boolean;
        requirementType?: InputRequirementType;
    }>(),
    {
        verified: undefined,
        disabled: false,
        allowAutocomplete: false,
        displayIcon: true,
    }
);

// Emits
const emit = defineEmits(["update:value", "submit", "onStartEditing", "onEndEditing"]);

// State
const inputValue = ref(props.value);
const isEditing = ref(false);
const isEmailValid = ref<boolean | undefined>(undefined);
const isVerified = ref<boolean | undefined>(props.verified);
const showError = ref(false);
const emailElement = ref<HTMLElement | undefined>();

// Computed
const isEdited = computed(() => inputValue.value !== undefined && inputValue.value.length > 0);
const hasVerificationResult = computed(() => isVerified.value !== undefined);
const isIconVisible = computed(() => props.displayIcon && !hasVerificationResult.value);
const isConfirmationStateVisible = computed(() => hasVerificationResult.value);
const isIconActive = computed(() => isEmailValid.value === true);

const containerClasses = computed(() => ({
    intent: props.indent,
    editing: isEditing.value,
    edited: isEdited.value,
    "has-error": showError.value,
    hasconfirmation: hasVerificationResult.value,
    disabled: props.disabled,
}));

const label = computed<string | undefined>(() => {
    const text = translations.profile.email ;

    if (props.requirementType === InputRequirementType.Optional) {
        return text + " (" + translations.addToCartPopover.optional + ")";
    }

    return text;
});

// Methods
function isValid(): boolean {
    if (props.requirementType === InputRequirementType.NotRequired || props.requirementType === InputRequirementType.Optional) {
        return inputValue.value == null || inputValue.value.length == 0 || Validation.isEmail(inputValue.value);
    }

    return inputValue.value !== undefined && Validation.isEmail(inputValue.value);
}

function setValue(email: string) {
    inputValue.value = email;
    checkEmailInput();
}

function getValue() {
    return isEdited.value ? inputValue.value : undefined;
}

function setVerified(ver?: boolean) {
    isVerified.value = ver;
}

function setError(error: boolean) {
    showError.value = error;
}

function focus() {
    if (emailElement.value) {
        $(emailElement.value).focus();
    }
}

// Event Handlers
function onInputKeyUp(e: KeyboardEvent) {
    checkEmailInput();
    emit("update:value", inputValue.value);

    if (isValid() && e.key === "Enter") {
        emit("submit");
    }
}

function onInputFocus() {
    isEditing.value = true;
}

function onInputBlur() {
    checkEmailInput(true);
    isEditing.value = false;
}

function checkEmailInput(removeWhiteSpace?: boolean) {
    if (inputValue.value && removeWhiteSpace) {
        inputValue.value = inputValue.value.replace(/\s+/g, "");
    }

    if (isVerified.value !== undefined) return;

    isEmailValid.value = isValid();

    if (isEmailValid.value) {
        showError.value = false;
    }
}

// Watchers
watch(
    () => props.value,
    (newValue) => {
        inputValue.value = newValue;
    }
);

watch(
    () => props.verified,
    (newValue) => {
        isVerified.value = newValue;
    }
);

watch(inputValue, () => {
    emit("update:value", inputValue.value);
});

watch(isEditing, (newValue) => {
    if (newValue)
        emit("onStartEditing");
    else
        emit("onEndEditing");
});

// Exposed API
defineExpose({
    isValid,
    setValue,
    getValue,
    setError,
    setVerified,
    focus,
});
</script>
